/* ============================================================
   AMAHC Landing Page block system - shared behaviour

   One controller for every blocks/lp-*.liquid file. It is loaded
   by each LP block, so this file WILL be included more than once
   on a page; the guard below makes that harmless.

   Nothing here is keyed to a section id. Blocks find their own
   behaviour through data- attributes, which is what lets an LP
   block work inside any section, including a PDP.
   ============================================================ */
(function () {
  if (window.__lpBlocksLoaded) return;
  window.__lpBlocksLoaded = true;

  var REDUCED =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */

  // Every init is idempotent: the theme editor re-renders blocks constantly,
  // and double-binding a submit handler would double-post to Klaviyo.
  function once(el, key) {
    var flag = 'lp' + key;
    if (el.dataset[flag]) return false;
    el.dataset[flag] = '1';
    return true;
  }

  function each(root, sel, fn) {
    Array.prototype.forEach.call(root.querySelectorAll(sel), fn);
  }

  /* ---------- reveal-on-load ---------- */

  function initReveal(root) {
    each(root, '.lp-rise', function (el) {
      if (!once(el, 'Rise')) return;
      if (REDUCED) {
        el.classList.add('lp-go');
        return;
      }
      if (!('IntersectionObserver' in window)) {
        el.classList.add('lp-go');
        return;
      }
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              e.target.classList.add('lp-go');
              io.unobserve(e.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px' }
      );
      io.observe(el);
    });
  }

  /* ---------- carousels (rail layout on a container block) ---------- */

  function initCarousel(root) {
    each(root, '[data-lp-carousel]', function (rail) {
      if (!once(rail, 'Carousel')) return;

      // Every LP block emits its own <link>/<script> tags, which land inside
      // this container as extra children. They are display:none so they cost
      // nothing visually, but counting them would give us a dot per asset tag
      // instead of a dot per slide.
      var items = Array.prototype.filter.call(rail.children, function (el) {
        return !/^(LINK|SCRIPT|STYLE|TEMPLATE|META|NOSCRIPT)$/.test(el.tagName);
      });
      if (items.length < 2) return;

      var dots = document.createElement('div');
      dots.className = 'lp-cdots';
      for (var i = 0; i < items.length; i++) {
        (function (idx) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'lp-cdot';
          b.setAttribute('aria-label', 'Go to slide ' + (idx + 1));
          b.addEventListener('click', function () {
            go(idx, true);
          });
          dots.appendChild(b);
        })(i);
      }
      rail.parentNode.appendChild(dots);

      var cur = 0,
        paused = false,
        scrollTimer,
        resumeTimer,
        autoTimer;

      function mark() {
        for (var k = 0; k < dots.children.length; k++) {
          dots.children[k].classList.toggle('lp-on', k === cur);
        }
      }

      function go(n, manual) {
        cur = (n + items.length) % items.length;
        rail.scrollTo({ left: items[cur].offsetLeft, behavior: 'smooth' });
        mark();
        if (manual) {
          paused = true;
          clearTimeout(resumeTimer);
          resumeTimer = setTimeout(function () {
            paused = false;
          }, 9000);
        }
      }

      rail.addEventListener('scroll', function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var best = 0,
            bd = 1e9;
          for (var k = 0; k < items.length; k++) {
            var d = Math.abs(items[k].offsetLeft - rail.scrollLeft);
            if (d < bd) {
              bd = d;
              best = k;
            }
          }
          cur = best;
          mark();
        }, 140);
      });
      rail.addEventListener('pointerenter', function () {
        paused = true;
      });
      rail.addEventListener('pointerleave', function () {
        paused = false;
      });
      mark();

      if (!REDUCED && rail.hasAttribute('data-lp-autoplay')) {
        autoTimer = setInterval(function () {
          if (!paused) go(cur + 1);
        }, 4500);
        // stop the timer if the rail is torn out by the theme editor
        if ('MutationObserver' in window) {
          new MutationObserver(function () {
            if (!document.contains(rail)) clearInterval(autoTimer);
          }).observe(document.documentElement, { childList: true, subtree: true });
        }
      }
    });
  }

  /* ---------- countdown ---------- */

  function initCountdown(root) {
    each(root, '[data-lp-deadline]', function (el) {
      if (!once(el, 'Countdown')) return;
      var end = new Date(el.getAttribute('data-lp-deadline')).getTime();
      var fallback = el.getAttribute('data-lp-fallback') || '';
      if (isNaN(end)) {
        el.textContent = fallback;
        return;
      }
      (function tick() {
        if (!document.contains(el)) return; // block removed in the editor
        var d = end - Date.now();
        if (d <= 0) {
          el.textContent = fallback;
          return;
        }
        var days = Math.floor(d / 864e5),
          h = Math.floor((d % 864e5) / 36e5),
          m = Math.floor((d % 36e5) / 6e4),
          s = Math.floor((d % 6e4) / 1e3);
        function pad(n) {
          return (n < 10 ? '0' : '') + n;
        }
        el.textContent =
          (days > 0 ? days + 'd ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s);
        setTimeout(tick, 1000);
      })();
    });
  }

  /* ---------- sticky bar ---------- */

  // Shows once the first hero (or a named anchor) has scrolled out of view.
  function initSticky(root) {
    each(root, '[data-lp-sticky]', function (bar) {
      if (!once(bar, 'Sticky')) return;
      var watchSel = bar.getAttribute('data-lp-sticky-watch') || '.lp-hero';
      var watch = document.querySelector(watchSel);
      if (!watch || !('IntersectionObserver' in window)) {
        bar.classList.add('lp-show');
        return;
      }
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          bar.classList.toggle('lp-show', !e.isIntersecting);
        });
      }).observe(watch);
    });
  }

  /* ---------- lead capture -> Klaviyo ---------- */

  // AU mobiles only. Returns E.164 or null; null on a required field blocks submit.
  function e164au(raw) {
    var d = String(raw || '').trim();
    if (d.charAt(0) === '+') return /^\+\d{8,15}$/.test(d) ? d : null;
    d = d.replace(/\D/g, '');
    if (d.charAt(0) === '0') d = d.slice(1);
    if (d.indexOf('61') === 0) d = d.slice(2);
    if (/^4\d{8}$/.test(d)) return '+61' + d;
    if (/^[2378]\d{8}$/.test(d)) return '+61' + d;
    return null;
  }

  function initLeadForms(root) {
    each(root, 'form[data-lp-klist]', function (f) {
      if (!once(f, 'Lead')) return;

      f.addEventListener('submit', function (e) {
        e.preventDefault();

        var old = f.querySelector('.lp-kmsg');
        if (old) old.remove();

        function msg(text, isError) {
          var m = document.createElement('div');
          m.className = 'lp-kmsg';
          m.style.cssText =
            'width:100%;font-size:13.5px;margin-top:4px;color:' +
            (isError ? '#ff8a80' : 'var(--lp-good)') +
            ' !important';
          m.textContent = text;
          f.appendChild(m);
        }

        var emailEl = f.querySelector('input[type=email]');
        var email = ((emailEl || {}).value || '').trim().toLowerCase();
        if (email) {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            msg('Please enter a valid email.', 1);
            return;
          }
        } else if (emailEl && emailEl.required) {
          msg('Please enter a valid email.', 1);
          return;
        }

        var phoneEl = f.querySelector('input[type=tel]'),
          phone = null;
        if (phoneEl) {
          phone = e164au(phoneEl.value);
          if (phoneEl.required && !phone) {
            msg('Please enter a valid Australian mobile.', 1);
            return;
          }
        }

        if (!email && !phone) {
          msg('Please add your mobile or email.', 1);
          return;
        }

        var companyId = f.getAttribute('data-lp-kkey');
        if (!companyId) {
          msg('This form is not connected yet. Please call (02) 9055 8231.', 1);
          return;
        }

        var attrs = {};
        if (email) attrs.email = email;
        if (phone) attrs.phone_number = phone;

        var src =
          (f.querySelector('input[name=campaign]') || {}).value ||
          (f.querySelector('input[name=source]') || {}).value ||
          'Landing page';

        var payload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: src,
              profile: { data: { type: 'profile', attributes: attrs } }
            },
            relationships: {
              list: { data: { type: 'list', id: f.getAttribute('data-lp-klist') } }
            }
          }
        };

        var btn = f.querySelector('button[type=submit]');
        if (btn) {
          btn.disabled = true;
          btn.style.opacity = '.6';
        }

        fetch(
          'https://a.klaviyo.com/client/subscriptions/?company_id=' +
            encodeURIComponent(companyId),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
            body: JSON.stringify(payload)
          }
        )
          .then(function (r) {
            if (!r.ok) throw new Error('klaviyo ' + r.status);
            var done = f.getAttribute('data-lp-success') || "You're on the list.";
            f.innerHTML =
              '<div class="lp-kdone"><span class="lp-ktick">✓</span><span>' +
              done.replace(/</g, '&lt;') +
              '</span></div>';
          })
          .catch(function () {
            msg(
              'Something went wrong. Please try again, or call us on (02) 9055 8231.',
              1
            );
            if (btn) {
              btn.disabled = false;
              btn.style.opacity = '';
            }
          });
      });
    });
  }

  /* ---------- Solid Oak table picker and bundle builder ---------- */

  function lpMoney(cents, showCents) {
    var amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: showCents ? 2 : 0,
        maximumFractionDigits: showCents ? 2 : 0
      }).format(amount);
    } catch (e) {
      var fallback = showCents ? amount.toFixed(2) : Math.round(amount).toString();
      var parts = fallback.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '$' + parts.join('.');
    }
  }

  function lpProductImage(product) {
    var image = product && product.featured_image;
    if (image && typeof image === 'object') image = image.src;
    if (!image && product && product.images && product.images.length) {
      image = product.images[0];
      if (image && typeof image === 'object') image = image.src;
    }
    if (image && image.indexOf('//') === 0) image = 'https:' + image;
    return image || '';
  }

  function lpProductVariant(product) {
    var variants = (product && product.variants) || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i];
    }
    return variants[0] || null;
  }

  var LP_PRODUCT_CACHE = {};

  function lpFetchProduct(handle) {
    if (LP_PRODUCT_CACHE[handle]) return LP_PRODUCT_CACHE[handle];
    LP_PRODUCT_CACHE[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Product request failed');
      return response.json();
    }).catch(function (error) {
      delete LP_PRODUCT_CACHE[handle];
      throw error;
    });
    return LP_PRODUCT_CACHE[handle];
  }

  function lpElementNodes(root, selector) {
    return Array.prototype.filter.call(root.querySelectorAll(selector), function (node) {
      return (
        node.nodeType === 1 &&
        !/^(LINK|SCRIPT|STYLE|TEMPLATE|META|NOSCRIPT)$/.test(node.tagName)
      );
    });
  }

  function initTablePickers(root) {
    each(root, '[data-lp-table-picker]', function (picker) {
      if (!once(picker, 'TablePicker')) return;

      var productLinks = lpElementNodes(picker, '[data-lp-picker-product]');
      var products = productLinks.map(function (link) {
        return {
          size: link.getAttribute('data-size') || '',
          frame: link.getAttribute('data-frame') || '',
          felt: link.getAttribute('data-felt') || '',
          handle: link.getAttribute('data-handle') || '',
          link: link
        };
      });
      if (!products.length) return;

      var selectors = picker.querySelector('[data-lp-picker-selectors]');
      var image = picker.querySelector('[data-lp-picker-image]');
      var title = picker.querySelector('[data-lp-picker-title]');
      var price = picker.querySelector('[data-lp-picker-price]');
      var addons = picker.querySelector('[data-lp-picker-addons]');
      var addonSummary = picker.querySelector('[data-lp-picker-addon-summary]');
      var shipLabel = picker.querySelector('[data-lp-picker-ship-label]');
      var shipDetail = picker.querySelector('[data-lp-picker-ship-detail]');
      var availability = picker.querySelector('[data-lp-picker-availability]');
      var status = picker.querySelector('[data-lp-picker-status]');
      var cta = picker.querySelector('[data-lp-picker-cta]');
      var ctaMode = cta.getAttribute('data-cta-mode') || 'bundle';
      var cartLink = picker.querySelector('[data-lp-picker-cart-link]');
      var gamesTrigger = picker.querySelector('[data-lp-games-open]');
      var pickerVariantInput = picker.querySelector('[data-lp-picker-variant-id]');
      var ctaLabel = picker.querySelector('[data-lp-picker-cta-label]');
      var details = picker.querySelector('[data-lp-picker-details]');
      var preorderCard = picker.querySelector('[data-lp-picker-preorder]');
      var preorderEta = picker.querySelector('[data-lp-picker-preorder-eta]');
      var preorderEtaOverride = preorderCard
        ? preorderCard.getAttribute('data-eta-override') || ''
        : '';
      var showPreorderKit =
        preorderCard && preorderCard.getAttribute('data-show-kit') === 'true';
      var standardCtaLabel = cta.getAttribute('data-standard-label') || 'Add table to my bundle';
      var productMap = {};
      var productMapNode = picker.querySelector('[data-lp-picker-product-map]');
      if (productMapNode) {
        try {
          productMap = JSON.parse(productMapNode.textContent || '{}');
        } catch (e) {
          productMap = {};
        }
      }
      var gamesMap = {};
      try {
        gamesMap = JSON.parse(picker.getAttribute('data-games-map') || '{}');
      } catch (e) {
        gamesMap = {};
      }
      var selected = {
        size: products[0].size,
        frame: products[0].frame,
        felt: products[0].felt
      };
      var requestNumber = 0;
      var liveProduct = null;
      var useAxis3 = picker.getAttribute('data-use-axis-3') !== 'false';
      var showSwatches = picker.getAttribute('data-show-swatches') !== 'false';
      var kinds = [
        { key: 'size', label: picker.getAttribute('data-axis-1-label') || 'Size' },
        { key: 'frame', label: picker.getAttribute('data-axis-2-label') || 'Frame' }
      ];
      if (useAxis3) {
        kinds.push({ key: 'felt', label: picker.getAttribute('data-axis-3-label') || 'Felt' });
      }

      function titleBracket(productTitle) {
        var value = String(productTitle || '');
        if (value.indexOf('[') >= 0) {
          var squarePart = value.split('[')[1];
          return squarePart ? squarePart.split(']')[0].trim() : '';
        }
        var lower = value.toLowerCase();
        if (
          value.indexOf('(') >= 0 &&
          (lower.indexOf('ship') >= 0 ||
            lower.indexOf('backorder') >= 0 ||
            lower.indexOf('back order') >= 0 ||
            lower.indexOf('pre-order') >= 0 ||
            lower.indexOf('preorder') >= 0)
        ) {
          var roundPart = value.split('(')[1];
          return roundPart ? roundPart.split(')')[0].trim() : '';
        }
        return '';
      }

      function isPreorderBracket(bracket) {
        var lower = String(bracket || '').toLowerCase();
        return (
          lower.indexOf('ship') >= 0 ||
          lower.indexOf('backorder') >= 0 ||
          lower.indexOf('back order') >= 0 ||
          lower.indexOf('pre-order') >= 0 ||
          lower.indexOf('preorder') >= 0
        );
      }

      function mappedProduct(handle) {
        var data = productMap[handle];
        if (!data || !data.variant_id) return null;
        return {
          handle: data.handle || handle,
          title: data.title || '',
          price: data.price,
          compare_at_price: data.compare_at_price,
          available: data.available,
          bracket: data.bracket || '',
          eta_first: data.eta_first || '',
          eta_second: data.eta_second || '',
          featured_image: data.featured_image || '',
          url: data.url || '/products/' + handle,
          addons: data.addons || [],
          variants: [
            {
              id: data.variant_id,
              price: data.price,
              compare_at_price: data.compare_at_price,
              available: data.variant_available !== false
            }
          ]
        };
      }

      function gamesAssetFor(value) {
        if (gamesMap[value]) return gamesMap[value];
        var wanted = String(value || '').toLowerCase();
        var keys = Object.keys(gamesMap);
        for (var i = 0; i < keys.length; i++) {
          if (keys[i].toLowerCase() === wanted) return gamesMap[keys[i]];
        }
        return '';
      }

      function updateGamesTrigger(row, productTitle) {
        if (!gamesTrigger || !row) return;
        var asset = gamesAssetFor(row.frame);
        if (asset) gamesTrigger.setAttribute('data-games-asset', asset);
        else gamesTrigger.removeAttribute('data-games-asset');
        // Strip the "(SHIPS IN 1 WEEK)" style suffix: it is lead-time noise in a
        // modal about which games are on the machine.
        var gamesLabel = String(productTitle || row.frame || row.handle || '')
          .replace(/\s*\((?:SHIPS|PRE-?ORDER|BACK ?ORDER)[^)]*\)/gi, '')
          .trim();
        gamesTrigger.setAttribute('data-games-title', gamesLabel);
      }

      function uniqueValues(key) {
        var values = [];
        products.forEach(function (product) {
          if (product[key] && values.indexOf(product[key]) < 0) values.push(product[key]);
        });
        return values;
      }

      function matchesValue(product, key, value) {
        return key === 'felt' && product[key] === '' ? true : product[key] === value;
      }

      function hasCombination(kind, value) {
        return products.some(function (product) {
          return kinds.every(function (item) {
            return matchesValue(
              product,
              item.key,
              item.key === kind ? value : selected[item.key]
            );
          });
        });
      }

      function currentRow() {
        for (var i = 0; i < products.length; i++) {
          if (kinds.every(function (kind) {
            return matchesValue(products[i], kind.key, selected[kind.key]);
          })) {
            return products[i];
          }
        }
        return null;
      }

      function selectFirstValidDownstream() {
        for (var i = 0; i < products.length; i++) {
          if (!matchesValue(products[i], kinds[0].key, selected[kinds[0].key])) continue;
          for (var j = 1; j < kinds.length; j++) {
            selected[kinds[j].key] = products[i][kinds[j].key];
          }
          return;
        }
      }

      function updateButtons() {
        each(selectors, '[data-lp-picker-option]', function (button) {
          var kind = button.getAttribute('data-kind');
          var value = button.getAttribute('data-value');
          var active = selected[kind] === value;
          button.classList.toggle('is-selected', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
          if (!useAxis3 && kind === 'size') {
            button.disabled = false;
          } else {
            button.disabled = !hasCombination(kind, value);
          }
        });
      }

      function selectedAddonItems() {
        var items = [];
        if (!addons) return items;
        each(addons, '[data-addon-select]', function (select) {
          var option = select.options[select.selectedIndex];
          if (!select.value || !option) return;
          items.push({
            id: select.value,
            quantity: 1,
            price: Number(option.getAttribute('data-addon-price') || 0)
          });
        });
        return items;
      }

      function updateAddonSummary() {
        if (!addonSummary) return;
        var items = selectedAddonItems();
        var total = 0;
        for (var i = 0; i < items.length; i++) total += items[i].price;
        addonSummary.textContent = total ? '+ ' + lpMoney(total, true) + ' in extras' : '';
        addonSummary.hidden = !total;
      }

      function renderAddons(addonProducts) {
        if (!addons) return;
        addons.innerHTML = '';
        addonProducts = addonProducts || [];
        addonProducts.forEach(function (addon) {
          var variants = addon.variants || [];
          if (!variants.length) return;
          var wrapper = document.createElement('div');
          var label = document.createElement('label');
          var select = document.createElement('select');
          var noThanks = document.createElement('option');
          wrapper.className = 'lp-picker-addon product-form__input product-form__input--dropdown variant-input-wrapper';
          wrapper.setAttribute('data-addon-selector', '');
          wrapper.setAttribute('data-addon-product-id', addon.id);
          label.className = 'lp-picker-addon__label';
          label.textContent = addon.title || '';
          select.className = 'lp-picker-addon__select';
          select.setAttribute('data-addon-select', '');
          noThanks.value = '';
          noThanks.selected = true;
          noThanks.textContent = 'No thanks';
          select.appendChild(noThanks);
          variants.forEach(function (variant) {
            var option = document.createElement('option');
            option.value = variant.id;
            option.setAttribute('data-addon-price', variant.price);
            option.textContent = variant.title + ' (+' + lpMoney(variant.price, true) + ')';
            select.appendChild(option);
          });
          select.addEventListener('change', updateAddonSummary);
          wrapper.appendChild(label);
          wrapper.appendChild(select);
          addons.appendChild(wrapper);
        });
        updateAddonSummary();
      }

      function setLoading(row) {
        liveProduct = null;
        cta.disabled = true;
        renderAddons([]);
        if (ctaMode === 'cart' && cartLink) cartLink.hidden = true;
        picker.classList.add('is-loading');
        picker.classList.remove('is-fetch-error');
        status.textContent = 'Loading current product';
        details.href = '/products/' + row.handle;
      }

      function showProduct(product, row, thisRequest) {
        var variant = lpProductVariant(product);
        if (!variant) throw new Error('No product variant');
        var productImage = lpProductImage(product);
        var bracket = product.bracket || titleBracket(product.title);
        var preorder = isPreorderBracket(bracket);
        var etaText = preorderEtaOverride || bracket || 'Pre-order timing to be confirmed';
        var canBuy = Boolean(product.available !== false && variant.available);
        var displayTitle = product.title || row.size + ' ' + row.frame + ' ' + row.felt;

        if (ctaMode === 'cart' && cartLink) cartLink.hidden = true;
        renderAddons(product.addons || []);
        title.textContent = displayTitle;
        updateGamesTrigger(row, displayTitle);
        if (pickerVariantInput) pickerVariantInput.value = variant.id;
        price.textContent = lpMoney(variant.price != null ? variant.price : product.price);
        if (shipLabel) shipLabel.textContent = preorder ? 'PRE-ORDER' : 'IN STOCK';
        if (shipDetail) {
          shipDetail.textContent = preorder ? bracket : '';
          shipDetail.hidden = !preorder;
        }
        availability.textContent = canBuy ? 'Available' : 'Currently unavailable';
        availability.classList.toggle('is-unavailable', !canBuy);
        availability.hidden = preorder;
        if (preorderCard) preorderCard.hidden = !preorder;
        if (preorderEta) preorderEta.textContent = etaText;
        if (ctaLabel) {
          ctaLabel.textContent = ctaMode === 'cart'
            ? standardCtaLabel
            : preorder
            ? 'PRE-ORDER TABLE TO MY BUNDLE'
            : standardCtaLabel;
        }
        status.textContent = '';
        details.href = product.url || '/products/' + row.handle;
        if (productImage) {
          var preloadedImage = new Image();
          preloadedImage.onload = function () {
            if (thisRequest !== requestNumber || !document.contains(picker)) return;
            image.src = productImage;
            image.alt = product.title || 'Selected pool table';
            image.hidden = false;
          };
          preloadedImage.src = productImage;
        }
        liveProduct = {
          variantId: variant.id,
          price: Number(variant.price != null ? variant.price : product.price || 0),
          title: product.title || row.size + ' ' + row.frame + ' ' + row.felt,
          handle: row.handle,
          size: row.size,
          preorder: preorder,
          bracket: bracket,
          eta: preorder ? etaText : bracket || 'In stock',
          fulfilment: preorder
            ? 'PRE-ORDER — not in stock, ships from incoming container'
            : 'In stock — ship now',
          offer: preorder ? 'Worth the Wait pre-order' : 'Standard',
          kitVariantId:
            preorder && showPreorderKit
              ? row.size.toLowerCase().indexOf('7ft') >= 0
                ? '45160062124171'
                : row.size.toLowerCase().indexOf('8ft') >= 0
                  ? '45160062156939'
                  : ''
              : ''
        };
        cta.disabled = !canBuy;
        picker.classList.remove('is-loading', 'is-fetch-error');
        picker.classList.add('is-live');
      }

      function loadCurrent() {
        var row = currentRow();
        if (!row) return;
        updateGamesTrigger(row, '');
        var thisRequest = ++requestNumber;
        var embeddedProduct = mappedProduct(row.handle);
        if (embeddedProduct) {
          showProduct(embeddedProduct, row, thisRequest);
          return;
        }
        setLoading(row);
        lpFetchProduct(row.handle)
          .then(function (product) {
            if (thisRequest !== requestNumber || !document.contains(picker)) return;
            showProduct(product, row, thisRequest);
          })
          .catch(function () {
            if (thisRequest !== requestNumber || !document.contains(picker)) return;
            picker.classList.remove('is-loading');
            picker.classList.add('is-fetch-error');
            status.textContent = 'Live details could not load. Use a product link below.';
            title.textContent = row.size + ' ' + row.frame + ' frame with ' + row.felt + ' cloth';
            updateGamesTrigger(row, title.textContent);
            price.textContent = '';
            if (shipLabel) shipLabel.textContent = 'VIEW PRODUCT';
            if (shipDetail) shipDetail.hidden = true;
            availability.textContent = '';
            availability.hidden = false;
            if (preorderCard) preorderCard.hidden = true;
            if (ctaLabel) ctaLabel.textContent = standardCtaLabel;
            cta.disabled = true;
          });
      }

      kinds.forEach(function (kind) {
        var group = document.createElement('fieldset');
        var legend = document.createElement('legend');
        var options = document.createElement('div');
        legend.textContent = kind.label;
        group.className = 'lp-picker-options lp-picker-options--' + kind.key;
        legend.className = 'lp-picker-options__label';
        options.className = 'lp-picker-options__row';
        uniqueValues(kind.key).forEach(function (value) {
          var button = document.createElement('button');
          var swatch = document.createElement('span');
          var buttonLabel = document.createElement('span');
          button.type = 'button';
          button.className = 'lp-picker-option lp-picker-option--' + kind.key;
          button.setAttribute('data-lp-picker-option', '');
          button.setAttribute('data-kind', kind.key);
          button.setAttribute('data-value', value);
          button.setAttribute('aria-pressed', 'false');
          if (showSwatches && kind.key !== 'size') {
            swatch.className = 'lp-picker-swatch lp-picker-swatch--' +
              value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            button.appendChild(swatch);
          }
          buttonLabel.textContent = value;
          button.appendChild(buttonLabel);
          button.addEventListener('click', function () {
            if (button.disabled || selected[kind.key] === value) return;
            selected[kind.key] = value;
            if (!useAxis3 && kind.key === 'size' && !currentRow()) {
              selectFirstValidDownstream();
            }
            updateButtons();
            loadCurrent();
          });
          options.appendChild(button);
        });
        group.appendChild(legend);
        group.appendChild(options);
        selectors.appendChild(group);
      });

      cta.addEventListener('click', function () {
        if (!liveProduct || cta.disabled) return;
        if (ctaMode === 'cart') {
          var fallbackUrl = details.href || '/products/' + liveProduct.handle;
          var cartItems = [{ id: liveProduct.variantId, quantity: 1 }];
          selectedAddonItems().forEach(function (item) {
            cartItems.push({ id: item.id, quantity: item.quantity });
          });
          cta.disabled = true;
          fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items: cartItems })
          })
            .then(function (response) {
              if (!response.ok) throw new Error('Cart add failed');
              return response.json();
            })
            .then(function () {
              if (ctaLabel) ctaLabel.textContent = 'ADDED ✓';
              if (cartLink) cartLink.hidden = false;
            })
            .catch(function () {
              window.location.href = fallbackUrl;
            });
          return;
        }
        window.lpBundleTable = {
          variantId: liveProduct.variantId,
          price: liveProduct.price,
          title: liveProduct.title,
          handle: liveProduct.handle,
          size: liveProduct.size,
          preorder: liveProduct.preorder,
          bracket: liveProduct.bracket,
          eta: liveProduct.eta,
          fulfilment: liveProduct.fulfilment,
          offer: liveProduct.offer,
          kitVariantId: liveProduct.kitVariantId
        };
        window.dispatchEvent(
          new CustomEvent('lp:table-picked', { detail: window.lpBundleTable })
        );
        var nextStep = document.getElementById('install-step') || document.getElementById('bundle-builder');
        if (nextStep) {
          var card = nextStep.querySelector('.lp-install__card') || nextStep;
          var head = nextStep.querySelector('.lp-install__head');
          lpScrollToStep(card, { context: head });
          // Tell the customer where to look next.
          lpHintRegions(nextStep);
        }
      });

      picker.classList.add('lp-table-picker--enhanced');
      updateButtons();
      loadCurrent();
    });
  }

  function initVideoCarousels(root) {
    each(root, '[data-lp-video-carousel]', function (carousel) {
      if (!once(carousel, 'VideoCarousel')) return;
      var rail = carousel.querySelector('[data-lp-video-rail]');
      if (!rail) return;

      var cards = lpElementNodes(rail, '[data-lp-video-card]');
      var videos = lpElementNodes(rail, '[data-lp-native-video]');
      var prev = carousel.querySelector('[data-lp-video-prev]');
      var next = carousel.querySelector('[data-lp-video-next]');
      var scrollTimer;

      function setSound(video, audible) {
        var card = video.closest('[data-lp-video-card]');
        var sound = card && card.querySelector('[data-lp-video-sound]');
        var mutedIcon = sound && sound.querySelector('[data-lp-video-muted]');
        var unmutedIcon = sound && sound.querySelector('[data-lp-video-unmuted]');
        var label = sound && sound.querySelector('[data-lp-video-sound-label]');
        video.muted = !audible;
        if (mutedIcon) mutedIcon.hidden = audible;
        if (unmutedIcon) unmutedIcon.hidden = !audible;
        if (label) label.textContent = audible ? 'Sound on' : 'Muted';
        video.setAttribute(
          'aria-label',
          (video.getAttribute('data-lp-video-label') || 'Testimonial video') +
            (audible ? '. Sound on' : '. Muted. Tap for sound')
        );
      }

      function muteOtherVideos(active) {
        videos.forEach(function (video) {
          if (video !== active) setSound(video, false);
        });
      }

      function plainProductLink(productNode) {
        var fallback = productNode.querySelector('[data-lp-video-product-fallback]');
        var live = productNode.querySelector('[data-lp-video-product-live]');
        if (live) live.hidden = true;
        if (fallback) fallback.hidden = false;
      }

      function hydrateProduct(productNode) {
        var handle = productNode.getAttribute('data-product-handle') || '';
        var fallback = productNode.querySelector('[data-lp-video-product-fallback]');
        var live = productNode.querySelector('[data-lp-video-product-live]');
        var image = productNode.querySelector('[data-lp-video-product-image]');
        var title = productNode.querySelector('[data-lp-video-product-title]');
        var price = productNode.querySelector('[data-lp-video-product-price]');
        var button = productNode.querySelector('[data-lp-video-atc]');
        var cart = productNode.querySelector('[data-lp-video-cart]');
        if (!handle || !fallback || !live || !button) return;

        lpFetchProduct(handle)
          .then(function (product) {
            var variant = lpProductVariant(product);
            if (!variant || !variant.available) throw new Error('No available variant');
            var productImage = lpProductImage(product);
            var shortTitle = String(product.title || 'View table')
              .replace(/\s*\(SHIPS IN[^)]*\)/gi, '')
              .trim();

            if (image) {
              if (productImage) {
                image.style.backgroundImage =
                  'url("' + String(productImage).replace(/"/g, '%22') + '")';
                image.setAttribute('role', 'img');
                image.setAttribute('aria-label', shortTitle);
                image.hidden = false;
              } else {
                image.hidden = true;
              }
            }
            if (title) title.textContent = shortTitle;
            if (price) {
              price.textContent = lpMoney(
                variant.price != null ? variant.price : product.price
              );
            }
            fallback.hidden = true;
            live.hidden = false;

            // Social proof should feed the funnel, not bypass it. Rather than
            // dropping the table straight in the cart (skipping install and the
            // accessory discount), preselect this exact table in step 1 and take
            // the customer there.
            if (cart) cart.hidden = true;
            button.addEventListener('click', function () {
              if (button.disabled) return;
              var picked = lpSelectTableByHandle(handle);
              if (!picked) {
                // No picker on the page, or no matching row: fall back to the PDP.
                window.location.href = '/products/' + handle;
                return;
              }
              button.classList.add('is-sent');
            });
          })
          .catch(function () {
            plainProductLink(productNode);
          });
      }

      function step() {
        if (!cards.length) return rail.clientWidth * 0.8;
        var style = window.getComputedStyle ? window.getComputedStyle(rail) : null;
        var gap = style ? parseFloat(style.columnGap || style.gap || 0) : 0;
        return cards[0].getBoundingClientRect().width + (isNaN(gap) ? 0 : gap);
      }

      function markArrows() {
        if (!prev || !next) return;
        var max = Math.max(0, rail.scrollWidth - rail.clientWidth - 2);
        prev.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= max;
      }

      if (prev && next && cards.length > 1) {
        prev.addEventListener('click', function () {
          rail.scrollBy({ left: -step(), behavior: REDUCED ? 'auto' : 'smooth' });
        });
        next.addEventListener('click', function () {
          rail.scrollBy({ left: step(), behavior: REDUCED ? 'auto' : 'smooth' });
        });
        rail.addEventListener('scroll', function () {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(markArrows, 80);
        });
        markArrows();
      }

      videos.forEach(function (video) {
        var videoCard = video.closest('[data-lp-video-card]');
        var errorPlaceholder = videoCard &&
          videoCard.querySelector('[data-lp-video-error-placeholder]');
        var soundIndicator = videoCard && videoCard.querySelector('[data-lp-video-sound]');
        var originalLabel = video.getAttribute('aria-label') || 'Testimonial video';
        video.setAttribute('data-lp-video-label', originalLabel);
        video.controls = false;
        video.tabIndex = 0;
        video.setAttribute('role', 'button');
        setSound(video, false);
        function showVideoError() {
          video.pause();
          video.hidden = true;
          if (soundIndicator) soundIndicator.hidden = true;
          if (errorPlaceholder) errorPlaceholder.hidden = false;
        }
        video.addEventListener('error', showVideoError);
        if (video.error) showVideoError();
        video.addEventListener('loadedmetadata', function () {
          if (!video.duration || video.currentTime > 0) return;
          try {
            video.currentTime = Math.min(0.01, video.duration / 2);
          } catch (e) {
            return;
          }
        });
        video.addEventListener('play', function () {
          if (!video.muted) muteOtherVideos(video);
        });

        function toggleSound() {
          if (video.muted) {
            muteOtherVideos(video);
            if (!video.getAttribute('data-lp-sound-used')) {
              try {
                video.currentTime = 0;
              } catch (e) {}
              video.setAttribute('data-lp-sound-used', 'true');
            }
            setSound(video, true);
            var playAttempt = video.play();
            if (playAttempt && playAttempt.catch) playAttempt.catch(function () {});
          } else {
            setSound(video, false);
          }
        }

        video.addEventListener('click', toggleSound);
        video.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          toggleSound();
        });
      });

      if (
        document.documentElement.classList.contains('lp-js') &&
        !REDUCED &&
        'IntersectionObserver' in window
      ) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var video = entry.target;
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                var playAttempt = video.play();
                if (playAttempt && playAttempt.catch) playAttempt.catch(function () {});
              } else {
                video.pause();
                setSound(video, false);
              }
            });
          },
          { threshold: [0, 0.6, 1] }
        );
        videos.forEach(function (video) {
          observer.observe(video);
        });
      }

      lpElementNodes(rail, '[data-lp-video-product]').forEach(hydrateProduct);
      carousel.classList.add('lp-video-carousel--enhanced');
    });
  }

  /* Scroll so a step's INTERACTIVE part is framed, not just the band's top edge.
     Measures the sticky theme header, prefers showing the whole control, and
     re-checks once lazy media above has settled and shifted the layout. */
  function lpStickyHeaderHeight() {
    var best = 0;
    var nodes = document.querySelectorAll('header,[class*="header"],[class*="Header"]');
    for (var i = 0; i < nodes.length && i < 40; i++) {
      var el = nodes[i], cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      var r = el.getBoundingClientRect();
      if (r.top <= 4 && r.height > best && r.height < 260) best = r.height;
    }
    return best;
  }

  function lpScrollToStep(target, opts) {
    if (!target) return;
    opts = opts || {};
    var gap = opts.gap == null ? 18 : opts.gap;
    var context = opts.context || null;   // optional heading to keep in view above

    function run(behavior) {
      var header = lpStickyHeaderHeight();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var rect = target.getBoundingClientRect();
      var top = rect.top + window.scrollY;
      var y = top - header - gap;

      // If the control fits, try to also show the heading that explains it.
      if (context) {
        var cRect = context.getBoundingClientRect();
        var cTop = cRect.top + window.scrollY;
        var needed = (top + rect.height) - cTop;
        if (needed < vh - header - gap * 2) y = cTop - header - gap;
      }
      // Never leave a SHORT control hanging off the bottom. A control taller than
      // the viewport can't be framed whole, so just align its top and stop there.
      var fits = rect.height <= (vh - header - gap * 2);
      if (fits) {
        var overflowBottom = (top + rect.height) - (y + vh);
        if (overflowBottom > 0) y += overflowBottom + gap;
      }

      // Safety net: whatever the maths says, never scroll past the control's own
      // top. Overshooting a step is worse than landing slightly high.
      var hardMax = top - header - 2;
      if (y > hardMax) y = hardMax;

      y = Math.max(0, Math.round(y));
      try { window.scrollTo({ top: y, behavior: behavior }); }
      catch (e) { window.scrollTo(0, y); }
    }

    run(REDUCED ? 'auto' : 'smooth');
    setTimeout(function () { run('auto'); }, 850);
  }

  /* One-shot arrival hint: telegraph that question 1 is the next thing to touch.
     Transform + opacity only, critically damped, staggered, fires once. Under
     prefers-reduced-motion it degrades to a plain cross-fade with no movement. */
  function lpHintRegions(section) {
    if (!section) return;
    var group = section.querySelector('[data-lp-install-regions], .lp-install__regions');
    if (!group || group.getAttribute('data-lp-hinted') === '1') return;
    group.setAttribute('data-lp-hinted', '1');
    var chips = lpElementNodes(group, '.lp-install__region');
    var step = section.querySelector('.lp-install__num');
    setTimeout(function () {
      if (step) {
        step.classList.remove('lp-hint-badge');
        void step.offsetWidth;
        step.classList.add('lp-hint-badge');
      }
      chips.forEach(function (chip, i) {
        chip.style.setProperty('--lp-hint-delay', (i * 55) + 'ms');
        chip.classList.remove('lp-hint-chip');
        void chip.offsetWidth;
        chip.classList.add('lp-hint-chip');
      });
      setTimeout(function () {
        chips.forEach(function (c) { c.classList.remove('lp-hint-chip'); c.style.removeProperty('--lp-hint-delay'); });
        if (step) step.classList.remove('lp-hint-badge');
      }, 1400);
    }, 480);
  }

  /* Preselect a table in step 1 from a product handle, then take the customer
     to it. Returns false if the picker or that combination is not on the page. */
  function lpSelectTableByHandle(handle) {
    if (!handle) return false;
    var picker = document.getElementById('table-picker') ||
                 document.querySelector('[data-lp-picker], .lp-table-picker');
    if (!picker) return false;
    var row = picker.querySelector('[data-lp-picker-product][data-handle="' + handle + '"]');
    if (!row) return false;

    var wanted = {
      size: row.getAttribute('data-size'),
      frame: row.getAttribute('data-frame'),
      felt: row.getAttribute('data-felt')
    };
    // Order matters: size gates which frames/felts are offered.
    ['size', 'frame', 'felt'].forEach(function (kind) {
      var value = wanted[kind];
      if (!value) return;
      var opts = lpElementNodes(picker, '[data-lp-picker-option][data-kind="' + kind + '"]');
      for (var i = 0; i < opts.length; i++) {
        var o = opts[i];
        if (String(o.getAttribute('data-value')).toLowerCase() !== String(value).toLowerCase()) continue;
        if (o.disabled) break;
        if (!/is-selected/.test(o.className)) o.click();
        break;
      }
    });

    // Anchor on the first thing they must touch. On mobile the picker stacks
    // image -> selectors -> CTA, so the CTA is a full screen below the controls;
    // aiming at it scrolls straight past the step.
    var head = picker.querySelector('.lp-table-picker__head');
    var controls = picker.querySelector('.lp-table-picker__selectors') ||
                   picker.querySelector('[data-lp-picker-option]') ||
                   picker.querySelector('[data-lp-picker-interactive]') || picker;
    lpScrollToStep(controls, { context: head });
    return true;
  }

  function initGamesList(root) {
    each(root, '[data-lp-games]', function (section) {
      if (!once(section, 'GamesList')) return;

      var modal = section.querySelector('[data-lp-games-modal]');
      var closeButton = section.querySelector('[data-lp-games-close]');
      var heading = section.querySelector('[data-lp-games-heading]');
      var search = section.querySelector('[data-lp-games-search]');
      var filters = section.querySelector('[data-lp-games-filters]');
      var count = section.querySelector('[data-lp-games-count]');
      var results = section.querySelector('[data-lp-games-results]');
      var currentGroup = section.querySelector('[data-lp-games-current-group]');
      var virtualList = section.querySelector('[data-lp-games-virtual]');
      var fallbackDataUrl = section.getAttribute('data-lp-games-url') || '';
      if (!modal || !search || !filters || !count || !results || !virtualList) return;

      var games = [];
      var matches = [];
      var activeConsole = '';
      var loaded = false;
      var dataCache = {};
      var dataRequest = 0;
      var returnFocus = null;
      var defaultHeading = heading ? heading.textContent : '';
      var searchTimer;
      var scrollQueued = false;
      var windowStart = -1;
      var rowHeight = 40;
      var windowSize = 220;
      var windowBuffer = 40;

      function formatNumber(value) {
        return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }

      function setState(message, countMessage) {
        if (currentGroup) currentGroup.hidden = true;
        virtualList.textContent = '';
        var state = document.createElement('p');
        state.className = 'lp-games__state';
        state.textContent = message;
        virtualList.appendChild(state);
        count.textContent = countMessage || '';
      }

      function updateStickyGroup(firstVisible) {
        if (!currentGroup) return;
        if (activeConsole || !matches.length) {
          currentGroup.hidden = true;
          currentGroup.textContent = '';
          return;
        }
        currentGroup.textContent = matches[Math.min(firstVisible, matches.length - 1)].console;
        currentGroup.hidden = false;
      }

      function renderWindow(force) {
        if (!matches.length) {
          setState('No games match that search.', count.textContent);
          windowStart = -1;
          return;
        }

        var stickyHeight = activeConsole ? 0 : 32;
        var firstVisible = Math.max(
          0,
          Math.floor(Math.max(0, results.scrollTop - stickyHeight) / rowHeight)
        );
        var start = Math.max(0, firstVisible - windowBuffer);
        var end = Math.min(matches.length, start + windowSize);
        updateStickyGroup(firstVisible);
        if (!force && start === windowStart) return;
        windowStart = start;

        var fragment = document.createDocumentFragment();
        var topSpacer = document.createElement('div');
        topSpacer.className = 'lp-games__spacer';
        topSpacer.style.height = start * rowHeight + 'px';
        topSpacer.setAttribute('aria-hidden', 'true');
        fragment.appendChild(topSpacer);

        for (var i = start; i < end; i++) {
          var row = document.createElement('div');
          var title = document.createElement('span');
          var consoleName = document.createElement('span');
          row.className = 'lp-games__row';
          row.setAttribute('role', 'listitem');
          title.className = 'lp-games__title';
          title.textContent = matches[i].title;
          title.title = matches[i].title;
          consoleName.className = 'lp-games__console';
          consoleName.textContent = matches[i].console;
          consoleName.title = matches[i].console;
          row.appendChild(title);
          row.appendChild(consoleName);
          fragment.appendChild(row);
        }

        var bottomSpacer = document.createElement('div');
        bottomSpacer.className = 'lp-games__spacer';
        bottomSpacer.style.height = (matches.length - end) * rowHeight + 'px';
        bottomSpacer.setAttribute('aria-hidden', 'true');
        fragment.appendChild(bottomSpacer);
        virtualList.textContent = '';
        virtualList.appendChild(fragment);
      }

      function updateCount(query) {
        if (query) {
          count.textContent = formatNumber(matches.length) + ' matching "' + search.value.trim() + '"';
        } else {
          count.textContent = formatNumber(matches.length) + (matches.length === 1 ? ' game' : ' games');
        }
      }

      function applyFilters() {
        if (!loaded) return;
        var query = search.value.trim().toLowerCase();
        matches = games.filter(function (game) {
          return (!activeConsole || game.console === activeConsole) &&
            (!query || game.search.indexOf(query) >= 0);
        });
        updateCount(query);
        results.scrollTop = 0;
        windowStart = -1;
        renderWindow(true);
      }

      function filterButtons() {
        return lpElementNodes(filters, '[data-lp-games-filter]');
      }

      function resetFilters() {
        var buttons = filterButtons();
        for (var i = buttons.length - 1; i > 0; i--) {
          buttons[i].parentNode.removeChild(buttons[i]);
        }
        activeConsole = '';
        if (buttons[0]) {
          buttons[0].classList.add('is-selected');
          buttons[0].setAttribute('aria-pressed', 'true');
        }
      }

      function selectFilter(button) {
        activeConsole = button.getAttribute('data-console') || '';
        filterButtons().forEach(function (item) {
          var selected = item === button;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        applyFilters();
      }

      function bindFilter(button) {
        button.addEventListener('click', function () {
          if (!loaded) return;
          selectFilter(button);
        });
      }

      function prepareData(data) {
        var consoles = Array.isArray(data.consoles) ? data.consoles.slice() : [];
        var sourceGames = Array.isArray(data.games) ? data.games : [];
        var buckets = {};
        var extraConsoles = [];
        var preparedGames = [];

        consoles.forEach(function (consoleName) {
          if (typeof consoleName === 'string' && !buckets[consoleName]) buckets[consoleName] = [];
        });
        sourceGames.forEach(function (game) {
          if (!game || typeof game.t !== 'string' || typeof game.c !== 'string') return;
          if (!buckets[game.c]) {
            buckets[game.c] = [];
            extraConsoles.push(game.c);
          }
          buckets[game.c].push({
            title: game.t,
            console: game.c,
            search: game.t.toLowerCase()
          });
        });
        consoles = consoles.concat(extraConsoles).filter(function (consoleName, index, values) {
          return typeof consoleName === 'string' && values.indexOf(consoleName) === index;
        });
        consoles.forEach(function (consoleName) {
          preparedGames = preparedGames.concat(buckets[consoleName] || []);
        });

        return { games: preparedGames, consoles: consoles };
      }

      function showData(data) {
        resetFilters();
        games = data.games;
        data.consoles.forEach(function (consoleName) {
          var button = document.createElement('button');
          button.className = 'lp-games__filter';
          button.type = 'button';
          button.setAttribute('data-lp-games-filter', '');
          button.setAttribute('data-console', consoleName);
          button.setAttribute('aria-pressed', 'false');
          button.textContent = consoleName;
          bindFilter(button);
          filters.appendChild(button);
        });
        search.value = '';
        loaded = true;
        applyFilters();
      }

      function resolveDataUrl(asset) {
        if (!asset) return fallbackDataUrl;
        if (/^(?:https?:)?\/\//.test(asset) || asset.charAt(0) === '/') return asset;
        var cleanFallback = fallbackDataUrl.split('?')[0];
        var slash = cleanFallback.lastIndexOf('/');
        return slash >= 0
          ? cleanFallback.slice(0, slash + 1) + encodeURIComponent(asset)
          : asset;
      }

      function loadData(dataUrl) {
        var thisRequest = ++dataRequest;
        loaded = false;
        games = [];
        matches = [];
        resetFilters();
        search.value = '';
        setState('Loading games list...', 'Loading games');
        if (!window.fetch || !dataUrl) {
          setState('The games list could not be loaded. Please try again later.', 'Games unavailable');
          return;
        }

        if (!dataCache[dataUrl]) {
          dataCache[dataUrl] = { data: null, error: false, promise: null };
          dataCache[dataUrl].promise = fetch(dataUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
          })
            .then(function (response) {
              if (!response.ok) throw new Error('Games list request failed');
              return response.json();
            })
            .then(function (data) {
              if (!data || !Array.isArray(data.games)) throw new Error('Invalid games list');
              dataCache[dataUrl].data = prepareData(data);
              return dataCache[dataUrl].data;
            })
            .catch(function (error) {
              dataCache[dataUrl].error = true;
              throw error;
            });
        }

        if (dataCache[dataUrl].data) {
          showData(dataCache[dataUrl].data);
          return;
        }
        if (dataCache[dataUrl].error) {
          setState('The games list could not be loaded. Please try again later.', 'Games unavailable');
          return;
        }
        dataCache[dataUrl].promise
          .then(function (data) {
            if (thisRequest !== dataRequest) return;
            showData(data);
          })
          .catch(function () {
            if (thisRequest !== dataRequest) return;
            setState('The games list could not be loaded. Please try again later.', 'Games unavailable');
          });
      }

      bindFilter(filters.querySelector('[data-lp-games-filter]'));
      search.addEventListener('input', function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(applyFilters, 120);
      });
      results.addEventListener('scroll', function () {
        if (!loaded || scrollQueued) return;
        scrollQueued = true;
        var schedule = window.requestAnimationFrame || function (callback) {
          return setTimeout(callback, 16);
        };
        schedule(function () {
          scrollQueued = false;
          renderWindow(false);
        });
      });
      modal.lpOpenGames = function (trigger) {
        if (!modal.showModal) return;
        returnFocus = trigger;
        if (heading) {
          heading.textContent = trigger.getAttribute('data-games-title') || defaultHeading;
        }
        if (!modal.open) modal.showModal();
        loadData(resolveDataUrl(trigger.getAttribute('data-games-asset') || ''));
        setTimeout(function () { search.focus(); }, 0);
      };
      if (closeButton) {
        closeButton.addEventListener('click', function () {
          modal.close();
        });
      }
      modal.addEventListener('click', function (event) {
        if (event.target === modal) modal.close();
      });
      modal.addEventListener('close', function () {
        if (returnFocus) returnFocus.focus();
        returnFocus = null;
      });
    });

    each(document, '[data-lp-games-open]', function (trigger) {
      if (!once(trigger, 'GamesOpen')) return;
      trigger.addEventListener('click', function () {
        var modal = document.querySelector('[data-lp-games-modal]');
        if (modal && modal.lpOpenGames) modal.lpOpenGames(trigger);
      });
    });
  }

  function initInstallStep(root) {
    each(root, '[data-lp-install]', function (section) {
      if (!once(section, 'InstallStep')) return;
      var regionBtns = lpElementNodes(section, '[data-lp-install-region]');
      var regionsGroup = section.querySelector('[data-lp-install-regions]');
      var lockHint = section.querySelector('[data-lp-install-lock-hint]');
      var choiceWrap = section.querySelector('[data-lp-install-choice-wrap]');
      var choiceBtns = lpElementNodes(section, '[data-lp-install-choice]');
      var modal = section.querySelector('[data-lp-install-modal]');
      var modalClose = section.querySelector('[data-lp-install-modal-close]');
      var returnFocus = null;
      var promptTimer;
      var offerFrom = Date.parse(section.getAttribute('data-offer-from') || '');
      var offerUntil = Date.parse(section.getAttribute('data-offer-until') || '');

      // Region install offers (region_offers setting). Display only: the real
      // discount comes from a code at checkout, so the window matches the code.
      function offerLive() {
        var now = Date.now();
        if (!isNaN(offerFrom) && now < offerFrom) return false;
        if (!isNaN(offerUntil) && now > offerUntil) return false;
        return true;
      }
      function applyRegionOffer(amount) {
        if (amount > 0 && !offerLive()) amount = 0;
        choiceBtns.forEach(function (choice) {
          var was = choice.querySelector('[data-lp-install-was]');
          var now = choice.querySelector('[data-lp-install-now]');
          if (!was || !now) return;
          var cents = Number(choice.getAttribute('data-price') || 0);
          if (amount > 0 && cents > 0) {
            was.hidden = false;
            now.textContent = lpMoney(Math.max(0, cents - Math.round(amount * 100)));
          } else {
            was.hidden = true;
            now.textContent = lpMoney(cents);
          }
        });
      }

      function announce() {
        try {
          document.dispatchEvent(new CustomEvent('lp:install-picked', { detail: window.lpBundleInstall || null }));
        } catch (e) {}
      }

      // deliveryOnly: region we do not install in. The install options stay off but
      // "delivery only" must remain pickable, otherwise closing the partner modal
      // leaves the customer with nothing they can choose.
      function setLocked(locked, deliveryOnly) {
        if (choiceWrap) choiceWrap.classList.toggle('is-locked', locked && !deliveryOnly);
        choiceBtns.forEach(function (btn) {
          // Deliberately NOT btn.disabled: a disabled button swallows the click,
          // so the locked-state prompt on the wrapper would never fire.
          var isNone = !btn.getAttribute('data-variant-id');
          var off = locked && !(deliveryOnly && isNone);
          if (off) btn.setAttribute('aria-disabled', 'true');
          else btn.removeAttribute('aria-disabled');
          btn.classList.toggle('is-unavailable', Boolean(deliveryOnly && !isNone));
        });
      }

      if (modal) {
        modal.addEventListener('click', function (event) {
          var anchor = event.target.closest && event.target.closest('a[href^="#powerful-form-"],a[href^="tel:"]');
          if (!anchor || !modal.contains(anchor) || !modal.open) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          modal.close();
          setTimeout(function () {
            anchor.click();
          }, 0);
        }, true);
        if (modalClose) {
          modalClose.addEventListener('click', function () {
            modal.close();
          });
        }
        modal.addEventListener('click', function (event) {
          if (event.target === modal) modal.close();
        });
        modal.addEventListener('close', function () {
          if (returnFocus) returnFocus.focus();
          returnFocus = null;
        });
      }

      if (choiceWrap) {
        choiceWrap.addEventListener('click', function (event) {
          if (!choiceWrap.classList.contains('is-locked')) return;
          event.preventDefault();
          if (lockHint) lockHint.hidden = false;
          if (!regionsGroup) return;
          clearTimeout(promptTimer);
          regionsGroup.classList.remove('is-prompt');
          void regionsGroup.offsetWidth;
          regionsGroup.classList.add('is-prompt');
          var regionRect = regionsGroup.getBoundingClientRect();
          if (regionRect.top < 0 || regionRect.bottom > window.innerHeight) {
            regionsGroup.scrollIntoView({
              behavior: REDUCED ? 'auto' : 'smooth',
              block: 'center'
            });
          }
          promptTimer = setTimeout(function () {
            regionsGroup.classList.remove('is-prompt');
          }, 900);
        });
      }

      regionBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          regionBtns.forEach(function (b) { b.classList.remove('is-selected'); });
          btn.classList.add('is-selected');
          var ok = btn.getAttribute('data-serviceable') !== 'false';
          setLocked(!ok, !ok);
          applyRegionOffer(ok ? (parseFloat(btn.getAttribute('data-offer')) || 0) : 0);
          if (lockHint) lockHint.hidden = true;
          if (regionsGroup) regionsGroup.classList.remove('is-prompt');
          if (!ok) {
            choiceBtns.forEach(function (b) { b.classList.remove('is-selected'); });
            window.lpBundleInstall = null;
            announce();
            if (modal && modal.showModal) {
              returnFocus = btn;
              modal.showModal();
            }
          }
        });
      });

      // Step 2 is done: hand the customer on to step 3, the same way the table
      // picker hands them to step 2. Lazy media above keeps shifting layout
      // while a smooth scroll runs, so correct once it has settled.
      function goToAccessories() {
        var next = document.getElementById('bundle-builder');
        if (!next) return;
        // The choices themselves are the point, not the band heading above them.
        var list = next.querySelector('[data-lp-accessory-list]') || next;
        var head = next.querySelector('.lp-bundle-builder__head');
        lpScrollToStep(list, { context: head });
      }

      choiceBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (choiceWrap && choiceWrap.classList.contains('is-locked')) return;
          if (btn.classList.contains('is-unavailable')) {
            // Install is not offered in their region: show the partner details again.
            if (modal && modal.showModal && !modal.open) { returnFocus = btn; modal.showModal(); }
            return;
          }
          // Re-clicking the option already chosen should not yank the page again.
          var isNewChoice = !btn.classList.contains('is-selected');
          choiceBtns.forEach(function (b) { b.classList.remove('is-selected'); });
          btn.classList.add('is-selected');
          var id = btn.getAttribute('data-variant-id');
          window.lpBundleInstall = id
            ? {
                variantId: id,
                price: Number(btn.getAttribute('data-price') || 0),
                title: btn.getAttribute('data-title') || 'Installation'
              }
            : null;
          announce();
          if (isNewChoice) goToAccessories();
        });
      });
      setLocked(true);
    });
  }

  function initCallButtons(root) {
    each(root, 'a[href^="tel:"]', function (link) {
      if (!once(link, 'CallBtn')) return;
      link.addEventListener('click', function (event) {
        var canDial = window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
        if (canDial) return; // real phone: let it dial
        var form = document.querySelector('a[href^="#powerful-form-"]');
        if (!form) return;
        event.preventDefault();
        form.click();
      });
    });
  }

  function initShippingFollowups(root) {
    each(root, '[data-lp-shipping-quote]', function (panel) {
      if (!once(panel, 'ShippingFollowup')) return;
      var container = panel.parentNode;
      var result = container && container.querySelector('[data-shipping-result]');
      if (!result || !panel || !('MutationObserver' in window)) return;

      var observer;

      function reveal() {
        if (!panel.hidden) return;
        panel.hidden = false;
        if (REDUCED || !window.requestAnimationFrame) {
          panel.classList.add('is-visible');
        } else {
          window.requestAnimationFrame(function () {
            panel.classList.add('is-visible');
          });
        }
        if (observer) observer.disconnect();
      }

      function checkResult() {
        var visible = result.style.display !== 'none' &&
          window.getComputedStyle(result).display !== 'none';
        if (!visible) return;
        // Only offer a better price once an actual price is on screen. The
        // calculator first shows a suburb picker for postcodes that span
        // several suburbs, and "think that looks steep?" beside a dropdown
        // reads as nonsense.
        var text = result.textContent || '';
        var hasRate = /\$\s?\d/.test(text) || /\bfree\b/i.test(text);
        if (hasRate) reveal();
      }

      observer = new MutationObserver(checkResult);
      observer.observe(result, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
      });
      checkResult();
    });
  }

  function initCallbackBranding(root) {
    var tmpl = root.querySelector('template[data-lp-callback-brand]');
    if (!tmpl || window.__lpCallbackBrandWired) return;
    window.__lpCallbackBrandWired = true;

    function brand(app) {
      if (!app || app.getAttribute('data-lp-branded')) return;
      app.setAttribute('data-lp-branded', '1');
      app.classList.add('lp-cbform');
      var header = app.querySelector('.header, .globo-heading');
      var host = header ? header.parentNode : app;
      var node = tmpl.content.cloneNode(true);
      host.insertBefore(node, header || host.firstChild);
      if (header) header.style.display = 'none';
      var desc = app.querySelector('.globo-description, .description');
      if (desc) desc.style.display = 'none';
    }

    function sweep() {
      Array.prototype.forEach.call(
        document.querySelectorAll('.globo-form-app, [class*="globo-form-app"]'),
        brand
      );
    }

    if ('MutationObserver' in window) {
      new MutationObserver(sweep).observe(document.body, { childList: true, subtree: true });
    }
    sweep();
  }

  function initHeroVideos(root) {
    each(root, '[data-lp-hero-video]', function (video) {
      if (!once(video, 'HeroVideo')) return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var mob = video.getAttribute('data-src-mobile');
      var desk = video.getAttribute('data-src-desktop');
      var useMobile = mob && window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
      var src = useMobile ? mob : desk;
      if (!src) return;
      video.setAttribute('preload', 'metadata');
      video.src = src;
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () {});
    });
  }

  function initReviewCarousels(root) {
    each(root, '[data-lp-grev]', function (section) {
      if (!once(section, 'GrevCarousel')) return;
      var rail = section.querySelector('[data-lp-grev-rail]');
      var prev = section.querySelector('[data-lp-grev-prev]');
      var next = section.querySelector('[data-lp-grev-next]');
      if (!rail) return;
      function step() {
        var card = rail.querySelector('.lp-grev__card');
        return card ? card.getBoundingClientRect().width + 16 : rail.clientWidth * 0.8;
      }
      function sync() {
        if (!prev || !next) return;
        var max = rail.scrollWidth - rail.clientWidth - 2;
        prev.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= max;
      }
      if (prev) prev.addEventListener('click', function () { rail.scrollLeft -= step(); });
      if (next) next.addEventListener('click', function () { rail.scrollLeft += step(); });
      rail.addEventListener('scroll', sync);
      window.addEventListener('resize', sync);
      sync();
    });
  }

  function initCallbackModals(root) {
    each(root, '[data-lp-callback-modal]', function (modal) {
      if (!once(modal, 'CallbackModal')) return;
      var section = modal.closest('.lp-specialist') || document;
      var openers = lpElementNodes(section, '[data-lp-callback-open]');
      var closers = lpElementNodes(modal, '[data-lp-callback-close]');
      function setOpen(open) {
        modal.hidden = !open;
        document.documentElement.classList.toggle('lp-modal-open', open);
      }
      openers.forEach(function (btn) {
        btn.addEventListener('click', function () { setOpen(true); });
      });
      closers.forEach(function (el) {
        el.addEventListener('click', function () { setOpen(false); });
      });
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !modal.hidden) setOpen(false);
      });
    });
  }

  function initBundleBuilders(root) {
    each(root, '[data-lp-bundle-builder]', function (builder) {
      if (!once(builder, 'BundleBuilder')) return;

      var cards = lpElementNodes(builder, '[data-lp-accessory]');
      var accessoryMore = builder.querySelector('[data-lp-accessory-more]');
      var accessoryToggle = builder.querySelector('[data-lp-accessory-toggle]');
      var codeTierNodes = lpElementNodes(builder, '[data-lp-tier]');
      var autoTierNodes = lpElementNodes(builder, '[data-lp-auto-tier]');
      var tableLine = builder.querySelector('[data-lp-bundle-table]');
      var lines = builder.querySelector('[data-lp-bundle-lines]');
      var total = builder.querySelector('[data-lp-bundle-total]');
      var saving = builder.querySelector('[data-lp-bundle-saving]');
      var nudge = builder.querySelector('[data-lp-bundle-nudge]');
      var cta = builder.querySelector('[data-lp-bundle-cta]');
      var helper = builder.querySelector('[data-lp-bundle-helper]');
      var progressFill = builder.querySelector('[data-lp-bundle-progress-fill]');
      var table = window.lpBundleTable || null;
      var requestedMode = builder.getAttribute('data-discount-mode');
      var discountMode =
        requestedMode === 'code' || requestedMode === 'flat'
          ? requestedMode
          : 'automatic';
      var autoPercent = parseFloat(builder.getAttribute('data-auto-percent')) || 10;
      autoPercent = Math.max(0, Math.min(100, autoPercent));
      var codeTiers = codeTierNodes
        .map(function (node) {
          return {
            min: parseInt(node.getAttribute('data-min'), 10) || 0,
            label: node.getAttribute('data-label') || '',
            code: node.getAttribute('data-code') || ''
          };
        })
        .filter(function (tier) {
          return tier.min > 0 && tier.label && tier.code;
        })
        .sort(function (a, b) {
          return a.min - b.min;
        });
      var autoTiers = autoTierNodes
        .map(function (node) {
          return {
            count: parseInt(node.getAttribute('data-count'), 10) || 0,
            percent: parseFloat(node.getAttribute('data-percent')) || 0,
            node: node
          };
        })
        .filter(function (tier) {
          return tier.count > 0 && tier.percent > 0;
        })
        .sort(function (a, b) {
          return a.count - b.count;
        });
      var cardModels = cards.map(function (card) {
        return {
          card: card,
          checkbox: card.querySelector('[data-lp-accessory-check]'),
          media: card.querySelector('[data-lp-accessory-media]'),
          name: card.querySelector('[data-lp-accessory-name]'),
          blurb: card.querySelector('[data-lp-accessory-blurb]'),
          price: card.querySelector('[data-lp-accessory-price]'),
          autoSaving: card.querySelector('[data-lp-accessory-auto-saving]'),
          error: card.querySelector('[data-lp-accessory-error]'),
          link: card.querySelector('[data-lp-accessory-link]'),
          count: card.querySelector('[data-lp-accessory-count]'),
          prev: card.querySelector('[data-lp-accessory-prev]'),
          next: card.querySelector('[data-lp-accessory-next]'),
          select: card.querySelector('[data-lp-accessory-select]'),
          strike: card.querySelector('[data-lp-accessory-strike]'),
          options: lpElementNodes(card, '[data-lp-accessory-option]').map(function (node) {
            return {
              handle: node.getAttribute('data-handle') || '',
              label: node.getAttribute('data-label') || '',
              blurb: node.getAttribute('data-blurb') || '',
              product: null,
              image: '',
              failed: false
            };
          }),
          index: 0
        };
      });

      if (accessoryMore && accessoryToggle) {
        accessoryToggle.addEventListener('click', function () {
          var expanded = accessoryToggle.getAttribute('aria-expanded') === 'true';
          accessoryMore.hidden = expanded;
          accessoryToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          accessoryToggle.textContent = expanded
            ? accessoryToggle.getAttribute('data-more-label')
            : accessoryToggle.getAttribute('data-less-label');
        });
      }

      function chosenAccessories() {
        return cardModels
          .map(function (model) {
            var checkbox = model.checkbox;
            if (!checkbox || !checkbox.checked || checkbox.disabled) return null;
            return {
              variantId: checkbox.getAttribute('data-variant-id'),
              price: Number(checkbox.getAttribute('data-price') || 0),
              title: checkbox.getAttribute('data-title') || ''
            };
          })
          .filter(Boolean);
      }

      function activeCodeTier(count) {
        var found = null;
        codeTiers.forEach(function (tier) {
          if (count >= tier.min) found = tier;
        });
        return found;
      }

      function nextCodeTier(count) {
        for (var i = 0; i < codeTiers.length; i++) {
          if (codeTiers[i].min > count) return codeTiers[i];
        }
        return null;
      }

      function activeAutoTier(count) {
        var found = null;
        autoTiers.forEach(function (tier) {
          if (count >= tier.count) found = tier;
        });
        return found;
      }

      function nextAutoTier(count) {
        for (var i = 0; i < autoTiers.length; i++) {
          if (autoTiers[i].count > count) return autoTiers[i];
        }
        return null;
      }

      function updateLadder(count) {
        var current = activeAutoTier(count);
        var upcoming = nextAutoTier(count);
        var maxCount = autoTiers.length ? autoTiers[autoTiers.length - 1].count : 1;
        if (progressFill) {
          progressFill.style.width =
            Math.max(0, Math.min(100, count / maxCount * 100)) + '%';
        }
        autoTiers.forEach(function (tier) {
          tier.node.style.left = Math.min(100, tier.count / maxCount * 100) + '%';
          tier.node.classList.toggle('is-current', tier === current);
          tier.node.classList.toggle('is-reached', count >= tier.count);
          tier.node.classList.toggle('is-next', tier === upcoming);
        });
      }

      function addSummaryLine(label, cents) {
        var row = document.createElement('div');
        var name = document.createElement('span');
        var value = document.createElement('strong');
        row.className = 'lp-bundle-summary__line';
        name.textContent = label;
        value.textContent = lpMoney(cents);
        row.appendChild(name);
        row.appendChild(value);
        lines.appendChild(row);
      }

      function updateAccessoryNudges(count, accessorySubtotal) {
        // Compact tiles only (they carry data-lp-accessory-strike): show each tile at the
        // price it costs inside the bundle. Selected tiles use the current tier, unselected
        // tiles the tier they would unlock if added. Classic tiles have no strike node.
        cardModels.forEach(function (model) {
          if (!model.strike || !model.price) return;
          var tileBox = model.checkbox;
          if (!tileBox || tileBox.disabled) {
            model.strike.textContent = '';
            return;
          }
          var basePrice = Number(tileBox.getAttribute('data-price') || 0);
          var tilePercent = 0;
          if (discountMode === 'flat') {
            tilePercent = autoPercent;
          } else if (discountMode === 'automatic') {
            var tileTier = activeAutoTier(tileBox.checked ? count : count + 1);
            tilePercent = tileTier ? tileTier.percent : 0;
          }
          if (tilePercent > 0 && basePrice > 0) {
            model.strike.textContent = lpMoney(basePrice);
            model.price.textContent = lpMoney(Math.round(basePrice * (100 - tilePercent) / 100));
          } else {
            model.strike.textContent = '';
            model.price.textContent = lpMoney(basePrice);
          }
        });

        var currentAuto = activeAutoTier(count);
        var currentPercent = currentAuto ? currentAuto.percent : 0;
        var currentSaving = Math.round(accessorySubtotal * currentPercent / 100);

        cardModels.forEach(function (model) {
          if (!model.autoSaving) return;
          var checkbox = model.checkbox;
          if (
            discountMode === 'code' ||
            !checkbox ||
            checkbox.checked ||
            checkbox.disabled
          ) {
            model.autoSaving.textContent = '';
            return;
          }

          var candidatePrice = Number(checkbox.getAttribute('data-price') || 0);
          var extraSaving = 0;
          if (discountMode === 'flat') {
            extraSaving = Math.round(candidatePrice * autoPercent / 100);
          } else {
            var afterTier = activeAutoTier(count + 1);
            var afterPercent = afterTier ? afterTier.percent : 0;
            var afterSaving = Math.round(
              (accessorySubtotal + candidatePrice) * afterPercent / 100
            );
            extraSaving = afterSaving - currentSaving;
          }
          model.autoSaving.textContent =
            extraSaving > 0 ? '+ save another ' + lpMoney(extraSaving) : '';
        });
      }

      function renderSummary() {
        var accessories = chosenAccessories();
        var count = accessories.length;
        var currentCode = activeCodeTier(count);
        var upcomingCode = nextCodeTier(count);
        var currentAuto = activeAutoTier(count);
        var upcomingAuto = nextAutoTier(count);
        var rrp = table ? Number(table.price || 0) : 0;
        var accessorySubtotal = 0;
        lines.textContent = '';

        if (table) {
          tableLine.textContent = table.title;
          addSummaryLine('Table', table.price);
        } else {
          tableLine.textContent = 'Pick your table above';
        }
        accessories.forEach(function (accessory) {
          rrp += accessory.price;
          accessorySubtotal += accessory.price;
          addSummaryLine(accessory.title, accessory.price);
        });
        total.textContent = lpMoney(rrp);

        if (discountMode === 'automatic') {
          var tierPercent = currentAuto ? currentAuto.percent : 0;
          var automaticSaving = Math.round(accessorySubtotal * tierPercent / 100);
          if (count && table) {
            saving.textContent =
              'You are saving ' +
              lpMoney(automaticSaving) +
              ' - applied automatically at checkout, no code needed';
          } else if (count) {
            saving.textContent =
              'Pick a table to unlock ' +
              lpMoney(automaticSaving) +
              ' in automatic accessory savings.';
          } else {
            saving.textContent = autoTiers.length
              ? 'Add an accessory to unlock ' + autoTiers[0].percent + '% off.'
              : 'Add an accessory to unlock automatic savings.';
          }

          if (upcomingAuto) {
            var autoRemaining = upcomingAuto.count - count;
            nudge.textContent =
              (currentAuto ? 'You are at ' + currentAuto.percent + '%. ' : '') +
              'Add ' +
              autoRemaining +
              (count ? ' more ' : ' ') +
              (autoRemaining === 1 ? 'accessory' : 'accessories') +
              ' to unlock ' +
              upcomingAuto.percent +
              '% off all of them.';
          } else if (currentAuto) {
            nudge.textContent =
              'You are at ' +
              currentAuto.percent +
              '%. Highest bundle saving unlocked.';
          } else {
            nudge.textContent = '';
          }
          updateLadder(count);
        } else if (discountMode === 'flat') {
          var flatSaving = Math.round(accessorySubtotal * autoPercent / 100);
          if (count && table) {
            saving.textContent =
              'You are saving ' +
              lpMoney(flatSaving) +
              ' - applied automatically at checkout, no code needed';
          } else if (count) {
            saving.textContent =
              'Pick a table to unlock ' +
              lpMoney(flatSaving) +
              ' in automatic accessory savings.';
          } else {
            saving.textContent =
              'Add an accessory to save ' + autoPercent + '% automatically.';
          }
          nudge.textContent = '';
        } else {
          if (currentCode) {
            saving.textContent =
              'Bundle discount applied at checkout with code ' +
              currentCode.code +
              '. You are saving ' +
              currentCode.label +
              '.';
          } else {
            saving.textContent = 'Add an accessory to unlock bundle savings.';
          }

          if (upcomingCode) {
            var remaining = upcomingCode.min - count;
            nudge.textContent =
              'Add ' +
              remaining +
              ' more ' +
              (remaining === 1 ? 'accessory' : 'accessories') +
              ' to save ' +
              upcomingCode.label +
              '.';
          } else if (currentCode) {
            nudge.textContent = 'You have unlocked the highest bundle tier.';
          } else {
            nudge.textContent = '';
          }
        }

        updateAccessoryNudges(count, accessorySubtotal);

        if (table) {
          var cartLines = [String(table.variantId) + ':1'];
          accessories.forEach(function (accessory) {
            cartLines.push(String(accessory.variantId) + ':1');
          });
          if (table.kitVariantId) {
            cartLines.push(String(table.kitVariantId) + ':1');
          }
          cta.href =
            '/cart/' +
            cartLines.join(',') +
            (discountMode === 'code' && currentCode
              ? '?discount=' + encodeURIComponent(currentCode.code)
              : '');
          cta.classList.remove('is-disabled');
          cta.setAttribute('aria-disabled', 'false');
          if (discountMode === 'automatic' || discountMode === 'flat') {
            helper.textContent = count
              ? 'The automatic accessory discount activates with this table.'
              : 'Add an accessory to receive the automatic discount.';
          } else {
            helper.textContent = currentCode
              ? 'Your discount code is carried into checkout.'
              : 'Add an accessory to qualify for a bundle discount.';
          }
        } else {
          cta.href = '#table-picker';
          cta.classList.add('is-disabled');
          cta.setAttribute('aria-disabled', 'true');
          helper.textContent = 'Pick a table above to continue.';
        }
      }

      function showCardOption(model, nextIndex) {
        if (!model.options.length) return;
        var normalisedIndex =
          (nextIndex + model.options.length) % model.options.length;
        var changedOption = normalisedIndex !== model.index;
        model.index = normalisedIndex;
        var option = model.options[model.index];
        var checkbox = model.checkbox;
        model.card.setAttribute('data-handle', option.handle);
        if (model.name) model.name.textContent = option.label;
        if (model.blurb) model.blurb.textContent = option.blurb;
        if (model.link) model.link.href = '/products/' + option.handle;
        if (model.count) {
          model.count.textContent = model.index + 1 + ' / ' + model.options.length;
        }
        if (model.select) model.select.value = String(model.index);
        if (model.error) model.error.textContent = '';

        if (!option.product) {
          checkbox.disabled = true;
          if (model.price && changedOption) {
            model.price.textContent = option.failed ? 'View product' : 'Loading';
          }
          if (option.failed && model.error) {
            model.error.textContent = 'Live details unavailable. View product.';
          }
          renderSummary();
          return;
        }

        var product = option.product;
        var variant = lpProductVariant(product);
        if (!variant || product.available === false || !variant.available) {
          checkbox.disabled = true;
          checkbox.checked = false;
          model.card.classList.remove('is-selected');
          if (model.price) model.price.textContent = 'Currently unavailable';
          if (model.error) model.error.textContent = 'Choose another option.';
          renderSummary();
          return;
        }

        var optionPrice = Number(
          variant.price != null ? variant.price : product.price || 0
        );
        checkbox.disabled = false;
        checkbox.setAttribute('data-variant-id', variant.id);
        checkbox.setAttribute('data-price', optionPrice);
        checkbox.setAttribute('data-title', option.label || product.title || option.handle);
        if (model.price) model.price.textContent = lpMoney(optionPrice);
        if (model.link) model.link.href = product.url || '/products/' + option.handle;
        model.card.classList.add('is-available');
        model.card.classList.remove('is-fetch-error');

        var productImage = option.image || lpProductImage(product);
        if (model.card.hasAttribute('data-lock-image') && model.media.querySelector('img')) {
          productImage = null;
        }
        if (productImage && model.media) {
          var preloadedImage = new Image();
          var shownIndex = model.index;
          preloadedImage.onload = function () {
            if (model.index !== shownIndex || !document.contains(model.card)) return;
            var image = document.createElement('img');
            image.className = 'lp-accessory__image';
            image.src = productImage;
            image.alt = option.label || product.title || 'Pool table accessory';
            image.loading = 'lazy';
            model.media.textContent = '';
            model.media.appendChild(image);
          };
          preloadedImage.src = productImage;
        }
        renderSummary();
      }

      cardModels.forEach(function (model) {
        if (!model.checkbox || !model.options.length) return;
        model.checkbox.addEventListener('change', function () {
          model.card.classList.toggle('is-selected', model.checkbox.checked);
          renderSummary();
        });
        if (model.prev) {
          model.prev.addEventListener('click', function () {
            showCardOption(model, model.index - 1);
          });
        }
        if (model.next) {
          model.next.addEventListener('click', function () {
            showCardOption(model, model.index + 1);
          });
        }
        if (model.select) {
          model.select.addEventListener('change', function () {
            showCardOption(model, parseInt(model.select.value, 10) || 0);
          });
        }

        model.options.forEach(function (option, optionIndex) {
          lpFetchProduct(option.handle)
            .then(function (product) {
              if (!document.contains(model.card)) return;
              option.product = product;
              option.image = lpProductImage(product);
              if (option.image) {
                var optionImage = new Image();
                optionImage.src = option.image;
              }
              if (model.index === optionIndex) showCardOption(model, optionIndex);
            })
            .catch(function () {
              if (!document.contains(model.card)) return;
              option.failed = true;
              if (model.index === optionIndex) {
                model.card.classList.add('is-fetch-error');
                showCardOption(model, optionIndex);
              }
            });
        });
        showCardOption(model, 0);
      });

      cta.addEventListener('click', function (event) {
        if (!table) {
          event.preventDefault();
          return;
        }
        if (!window.fetch) return;
        event.preventDefault();
        if (cta.getAttribute('data-lp-adding') === 'true') return;

        var fallbackHref = cta.href;
        var accessories = chosenAccessories();
        var preorder = Boolean(table.preorder);
        var fulfilment = table.fulfilment ||
          (preorder
            ? 'PRE-ORDER — not in stock, ships from incoming container'
            : 'In stock — ship now');
        var dispatch = table.eta || table.bracket || (preorder ? 'Pre-order' : 'In stock');
        var items = [
          {
            id: Number(table.variantId),
            quantity: 1,
            properties: {
              Fulfilment: fulfilment,
              Offer: table.offer || (preorder ? 'Worth the Wait pre-order' : 'Standard'),
              'Est. dispatch': dispatch
            }
          }
        ];

        accessories.forEach(function (accessory) {
          items.push({
            id: Number(accessory.variantId),
            quantity: 1,
            properties: {
              Fulfilment: fulfilment,
              Offer: 'Solid Oak bundle',
              'Est. dispatch': dispatch
            }
          });
        });
        if (table.kitVariantId) {
          items.push({
            id: Number(table.kitVariantId),
            quantity: 1,
            properties: {
              Fulfilment: fulfilment,
              Offer: 'Worth the Wait pre-order',
              'Est. dispatch': dispatch
            }
          });
        }
        if (window.lpBundleInstall && window.lpBundleInstall.variantId) {
          items.push({
            id: Number(window.lpBundleInstall.variantId),
            quantity: 1,
            properties: {
              Fulfilment: fulfilment,
              Offer: 'Installation booked with table',
              'Est. dispatch': dispatch
            }
          });
        }

        cta.setAttribute('data-lp-adding', 'true');
        cta.setAttribute('aria-disabled', 'true');
        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items: items })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Bundle cart request failed');
            return response.json();
          })
          .then(function () {
            var currentCode = activeCodeTier(accessories.length);
            window.location.assign(
              discountMode === 'code' && currentCode
                ? '/cart?discount=' + encodeURIComponent(currentCode.code)
                : '/cart'
            );
          })
          .catch(function () {
            window.location.assign(fallbackHref);
          });
      });
      window.addEventListener('lp:table-picked', function (event) {
        if (!document.contains(builder)) return;
        table = event.detail || window.lpBundleTable || null;
        renderSummary();
      });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              builder.classList.toggle('is-active', entry.isIntersecting);
              document.documentElement.classList.toggle(
                'lp-bundle-active',
                entry.isIntersecting
              );
            });
          },
          { threshold: 0, rootMargin: '-8% 0px -8% 0px' }
        ).observe(builder);
      }

      builder.classList.add('lp-bundle-builder--enhanced');
      builder.classList.add('lp-bundle-builder--' + discountMode);
      renderSummary();
    });
  }

  /* ---------- boot ---------- */

  function init(root) {
    root = root || document;
    initReveal(root);
    initCarousel(root);
    initCountdown(root);
    initSticky(root);
    initLeadForms(root);
    initTablePickers(root);
    initVideoCarousels(root);
    initBundleBuilders(root);
    initCallbackModals(root);
    initReviewCarousels(root);
    initHeroVideos(root);
    initCallbackBranding(root);
    initCallButtons(root);
    initShippingFollowups(root);
    initInstallStep(root);
    initGamesList(root);
  }

  // "Pick my colours" style links jump to #table-picker. A native anchor lands on
  // the band's top padding and cuts the picker card off, so frame the card instead.
  if (!window.__lpPickerAnchor) {
    window.__lpPickerAnchor = 1;
    document.addEventListener('click', function (event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      var link = event.target.closest && event.target.closest('a[href="#table-picker"]');
      if (!link) return;
      var picker = document.getElementById('table-picker');
      if (!picker) return;
      var card = picker.querySelector('.lp-table-picker__card') || picker;
      var head = picker.querySelector('.lp-table-picker__head');
      event.preventDefault();
      lpScrollToStep(card, { context: head });
      try { history.replaceState(null, '', '#table-picker'); } catch (e) {}
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-init whatever was just re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    init(e.target);
  });
})();
